/**
 * Standalone outreach frameworks for template AI generation.
 * Personas (Alex / Rob) stay in emailPersonalities.js for send-time modes.
 * Here the user picks a psychological mail structure, not a guru.
 *
 * Two tiers:
 *   'play'    — the 2026 signature plays. They carry a skeleton, named slots the
 *               user can fill, subject rules and hard guardrails.
 *   'classic' — the original structural frameworks. Prompt text only.
 *
 * A slot the user fills becomes FIXED text in the generated template.
 * A slot left empty becomes a {single brace AI instruction} that the downstream
 * per-lead AI resolves from web research / custom fields at send time.
 */

function L(en, de) {
  return { English: en, German: de };
}

function S(en, de, weight) {
  return { label: L(en, de), weight };
}

/**
 * @param key    token used in the skeleton as [[key]]
 * @param icon   font-awesome class for the input row
 * @param auto   what the downstream per-lead AI must produce when the slot is empty
 */
function SLOT(key, icon, en, de, phEn, phDe, auto) {
  return { key, icon, label: L(en, de), placeholder: L(phEn, phDe), auto };
}

const emailFrameworks = [
  // ══════════════════════════ 2026 SIGNATURE PLAYS ══════════════════════════
  {
    id: 'sppo',
    tier: 'play',
    icon: 'fa-satellite-dish',
    badge: L('Best all-rounder 2026', 'Bester Allrounder 2026'),
    name: L('SPPO — Signal · Problem · Proof · Offer', 'SPPO — Anlass · Problem · Beweis · Angebot'),
    why: L(
      'A live business signal earns the first line. Everything after it has to pay that off.',
      'Ein echtes Business-Signal verdient die erste Zeile. Alles danach muss das einlösen.',
    ),
    bestFor: L(
      'General B2B, signal-based lists, services, SaaS.',
      'General B2B, signalbasierte Listen, Services, SaaS.',
    ),
    source: 'Cross-source synthesis: Gong / 30MPC / Outreach / Smartlead',
    steps: [
      S('Signal', 'Anlass', 3),
      S('Problem', 'Problem', 3),
      S('Proof', 'Beweis', 2),
      S('Offer CTA', 'Angebotsfrage', 2),
    ],
    subjectRule: '2–4 words lifted from the prospect\'s own world — their priority or their problem. No verbs, no pitch, no company name of the sender.',
    skeleton: `{{greeting}}

Saw [[signal]].

Teams at this stage usually run into [[problem]].

We recently helped [[proof]] — without [[tradeoff]].

I pulled together [[asset]] for {{business_name}} — want me to send it over?`,
    slots: [
      SLOT('signal', 'fa-broadcast-tower',
        'Signal', 'Anlass',
        'e.g. they just opened a second warehouse', 'z.B. sie haben ein zweites Lager eröffnet',
        'find ONE concrete, recent business event or stated priority for this lead (hiring push, new market, funding, new product, site relaunch, tooling change) and name it in max 10 words'),
      SLOT('problem', 'fa-exclamation-triangle',
        'Problem it causes', 'Problem, das daraus folgt',
        'e.g. picking errors climb once SKUs pass 5k', 'z.B. Kommissionierfehler steigen ab 5k SKUs',
        'name the specific friction, cost or risk this signal creates for this exact role and company size — a consequence, not a category. Max 15 words'),
      SLOT('proof', 'fa-award',
        'Peer proof', 'Beweis von Vergleichbaren',
        'e.g. a 40-person 3PL cut mispicks by a third', 'z.B. ein 40-Mann-3PL senkte Fehlpicks um ein Drittel',
        'pick the closest comparable customer type to this lead and state one credible outcome — a peer they would recognise as "like us". Max 15 words'),
      SLOT('tradeoff', 'fa-ban',
        'Without this trade-off', 'Ohne diesen Kompromiss',
        'e.g. without replacing their WMS', 'z.B. ohne ihr WMS zu ersetzen',
        'name the painful trade-off this lead would expect and that they do NOT have to make. Max 8 words'),
      SLOT('asset', 'fa-file-download',
        'What you offer to send', 'Was du anbietest zu schicken',
        'e.g. a 2-page mispick benchmark', 'z.B. ein 2-seitiger Fehlpick-Benchmark',
        'name a lightweight, specific artefact tailored to this lead (benchmark, teardown, audit, example set) — something that is clearly already made. Max 10 words'),
    ],
    rules: [
      'The signal must be externally observable. If nothing is observable, the {AI instruction} must tell the downstream AI to skip the sentence rather than invent one.',
      'Problem before proof. Never lead with the proof.',
      'One proof point only. A second one reads as a brochure.',
    ],
    prompt: `FRAMEWORK: SPPO
Signal → Problem → Proof → Offer. One live signal, the friction it creates, one peer outcome, then offer to send something useful.`,
  },
  {
    id: 'problem-prompter',
    tier: 'play',
    icon: 'fa-chess-king',
    badge: L('Best C-suite play', 'Bestes Framework für die Geschäftsleitung'),
    name: L('Problem Prompter', 'Problem sichtbar machen'),
    why: L(
      'Respects the goal they already announced, then shows the problem hiding behind it.',
      'Nimmt das Ziel ernst, das sie schon verkündet haben — und zeigt das Problem dahinter.',
    ),
    bestFor: L(
      'Enterprise, strategic initiatives, high ACV, C-level and VP.',
      'Enterprise, strategische Initiativen, hoher ACV, C-Level und VP.',
    ),
    source: 'Gong — Problem Prompter (Jen Allen-Knuth)',
    steps: [
      S('Saw this', 'Hab gesehen', 2),
      S('Up against X', 'Problem dahinter', 3),
      S('Status quo', 'Ist-Zustand', 2),
      S('Open to Y?', 'Offen für Y?', 2),
      S('Either way', 'So oder so', 1),
    ],
    subjectRule: 'Name the strategic priority in 2–4 words, exactly as they would phrase it internally. No question mark, no hook.',
    skeleton: `{{greeting}}

Saw {{business_name}} is pushing [[objective]] this year.

A similar [[peer]] found [[hiddenProblem]] made that harder — even though they were already [[statusQuo]].

They changed [[shift]] and got [[outcome]].

Open to the short breakdown of how they did it? Either way, congrats on the move.`,
    slots: [
      SLOT('objective', 'fa-flag',
        'Strategic objective', 'Strategisches Ziel',
        'e.g. expanding into DACH enterprise', 'z.B. Expansion ins DACH-Enterprise',
        'find the strategic objective this company has publicly committed to this year (from press, careers page, investor updates, leadership posts) and state it in their own words, max 8 words'),
      SLOT('peer', 'fa-users',
        'Comparable peer', 'Vergleichbares Unternehmen',
        'e.g. another Series-B fintech', 'z.B. ein anderes Series-B-Fintech',
        'describe the peer company type in a way this lead recognises as their own league — stage, size and market, not a brand name unless it is safe to use. Max 8 words'),
      SLOT('hiddenProblem', 'fa-search-minus',
        'Hidden problem behind the goal', 'Verstecktes Problem hinter dem Ziel',
        'e.g. onboarding time doubled per new region', 'z.B. Onboarding-Zeit verdoppelte sich pro Region',
        'name the second-order problem that shows up while chasing this objective — the one they have not budgeted for. It must be non-obvious. Max 15 words'),
      SLOT('statusQuo', 'fa-tools',
        'Status quo they already have', 'Ist-Zustand, den sie schon haben',
        'e.g. running a full RevOps team', 'z.B. mit einem kompletten RevOps-Team',
        'name the competent thing this lead is already doing, so the mail acknowledges it instead of talking down to them. Max 8 words'),
      SLOT('shift', 'fa-random',
        'The one thing they changed', 'Die eine Sache, die sie änderten',
        'e.g. moved QA to the first week', 'z.B. QA in die erste Woche gezogen',
        'state ONE operating principle that changed — a decision, not a product. Max 10 words'),
      SLOT('outcome', 'fa-chart-line',
        'Outcome', 'Ergebnis',
        'e.g. ramp cut from 90 to 40 days', 'z.B. Ramp von 90 auf 40 Tage',
        'give the concrete outcome with a number or timeframe, sized realistically to this lead. Max 10 words'),
    ],
    rules: [
      'Never criticise the initiative. The mail congratulates it and complicates it.',
      'The status-quo line is mandatory — it is what stops a VP from filing this as vendor noise.',
      'Close low-pressure: "Either way, congrats" must survive a no.',
    ],
    prompt: `FRAMEWORK: Problem Prompter
Saw the goal → the hidden problem behind it → acknowledge their status quo → one thing a peer changed → low-pressure close.`,
  },
  {
    id: 'four-sentence',
    tier: 'play',
    icon: 'fa-list-ol',
    badge: L('Most scalable for teams', 'Am besten skalierbar für Teams'),
    name: L('4-Sentence Problem Email', '4-Satz-Problem-Mail'),
    why: L(
      'Four sentences, four jobs. The format a whole SDR team can run without drifting.',
      'Vier Sätze, vier Aufgaben. Das Format, das ein ganzes SDR-Team ohne Drift fährt.',
    ),
    bestFor: L(
      'Mid-market, SDR/BDR teams, systematic outbound at volume.',
      'Mid-Market, SDR-/BDR-Teams, systematisches Outbound in Menge.',
    ),
    source: '30 Minutes to President\'s Club — Four-Sentence Problem Email',
    steps: [
      S('Personalization', 'Personalisierung', 2),
      S('Problem', 'Problem', 3),
      S('Solution', 'Lösung', 2),
      S('Test-drive CTA', 'Zum Ausprobieren', 2),
    ],
    subjectRule: 'A 2–4 word priority. Lowercase is fine. It should read like an internal Slack thread title, not a campaign.',
    skeleton: `{{greeting}}

Noticed [[signal]].

That usually creates [[problem]] for [[role]], because [[mechanism]].

We help [[peerGroup]] [[plainOutcome]].

Happy to share [[asset]] — useful?`,
    slots: [
      SLOT('signal', 'fa-eye',
        'What you noticed', 'Was dir aufgefallen ist',
        'e.g. three open ops roles on their careers page', 'z.B. drei offene Ops-Stellen auf der Karriereseite',
        'state one concrete account signal you can actually verify for this lead. Max 10 words. If nothing verifiable exists, drop the sentence entirely'),
      SLOT('problem', 'fa-bug',
        'Business consequence', 'Geschäftliche Folge',
        'e.g. handover gaps between shifts', 'z.B. Übergabelücken zwischen den Schichten',
        'name the business consequence, not the symptom. Max 12 words'),
      SLOT('role', 'fa-id-badge',
        'Whose problem it is', 'Wessen Problem es ist',
        'e.g. the head of operations', 'z.B. für die Ops-Leitung',
        'name the role that personally owns this pain at this company. Max 5 words'),
      SLOT('mechanism', 'fa-cogs',
        'Why it happens', 'Warum es passiert',
        'e.g. the roster lives in two systems', 'z.B. der Dienstplan liegt in zwei Systemen',
        'explain the mechanism in one plain clause — this is the sentence that proves you understand their operation. Max 12 words'),
      SLOT('peerGroup', 'fa-layer-group',
        'Who you help', 'Wem ihr helft',
        'e.g. multi-site logistics teams', 'z.B. Logistikteams mit mehreren Standorten',
        'describe the peer group this lead belongs to. Max 6 words'),
      SLOT('plainOutcome', 'fa-check-double',
        'Outcome in plain words', 'Ergebnis in Klartext',
        'e.g. close shifts without chasing anyone', 'z.B. Schichten abschließen, ohne hinterherzulaufen',
        'state the outcome in language a non-buyer would understand. Zero jargon, zero product names. Max 10 words'),
      SLOT('asset', 'fa-share-square',
        'Test-drive asset', 'Zum Ausprobieren',
        'e.g. the shift-handover teardown we use', 'z.B. das Schichtübergabe-Teardown, das wir nutzen',
        'name the thing that is worth receiving even if they never buy — a benchmark, teardown, checklist or example set. Max 10 words'),
    ],
    rules: [
      'Exactly four sentences in the body. If a fifth appears, merge or cut.',
      'Sentence 3 must contain no jargon and no product name.',
      'The CTA sells the exchange, not the meeting: it has to be worth a reply even with no purchase intent.',
    ],
    prompt: `FRAMEWORK: 4-Sentence Problem Email
Personalization → problem → plain-language solution → test-drive CTA. Exactly four sentences.`,
  },
  {
    id: 'poke-the-bear',
    tier: 'play',
    icon: 'fa-question-circle',
    badge: L('Strongest status-quo breaker', 'Bricht den Ist-Zustand am stärksten'),
    name: L('Poke the Bear / Knowledge Gap', 'Wissenslücke'),
    why: L(
      'One neutral question that makes a hole in their process visible to them.',
      'Eine neutrale Frage, die ihnen ein Loch im eigenen Prozess sichtbar macht.',
    ),
    bestFor: L(
      'Prospects with no active project and latent pain they have not named yet.',
      'Prospects ohne aktives Projekt, mit latentem, noch unbenanntem Schmerz.',
    ),
    source: 'Josh Braun — Poke the Bear',
    steps: [
      S('Neutral gap question', 'Neutrale Lückenfrage', 4),
      S('Peer pattern', 'Muster bei Vergleichbaren', 2),
      S('Diagnostic offer', 'Diagnose-Angebot', 2),
    ],
    subjectRule: 'Name the blind spot in 2–4 neutral words. Never phrase the subject as the leading question itself.',
    skeleton: `{{greeting}}

how are you checking whether [[hiddenRisk]] happens between [[checkpoints]]?

We keep seeing [[peerGroup]] discover [[cost]] only once [[laggingIndicator]] shows up.

I have a short diagnostic with [[checkCount]] checks for exactly this — want it?`,
    slots: [
      SLOT('hiddenRisk', 'fa-exclamation',
        'Hidden risk', 'Verstecktes Risiko',
        'e.g. a quote goes out with stale pricing', 'z.B. ein Angebot rausgeht mit veralteten Preisen',
        'name a specific thing that can silently go wrong inside their existing process. It must be checkable, not philosophical. Max 12 words'),
      SLOT('checkpoints', 'fa-map-signs',
        'Between which two steps', 'Zwischen welchen zwei Schritten',
        'e.g. between CRM export and the signed order', 'z.B. zwischen CRM-Export und unterschriebenem Auftrag',
        'name the two real checkpoints in their current process that the risk falls between. Use their vocabulary. Max 12 words'),
      SLOT('peerGroup', 'fa-users',
        'Peer group', 'Vergleichsgruppe',
        'e.g. distributors on legacy ERPs', 'z.B. Händler auf Legacy-ERPs',
        'describe the peer group in terms this lead identifies with. Max 6 words'),
      SLOT('cost', 'fa-euro-sign',
        'Cost or risk they discover', 'Kosten oder Risiko, das sie entdecken',
        'e.g. margin lost on 6% of orders', 'z.B. Marge verloren bei 6% der Aufträge',
        'quantify what it costs when it goes unnoticed, sized conservatively to this company. Max 10 words'),
      SLOT('laggingIndicator', 'fa-hourglass-end',
        'Lagging indicator', 'Nachlaufender Indikator',
        'e.g. the quarterly margin review', 'z.B. das Quartals-Margenreview',
        'name the late signal that finally exposes the problem — the thing that arrives too late to fix it. Max 8 words'),
      SLOT('checkCount', 'fa-tasks',
        'Number of checks', 'Anzahl der Prüfungen',
        'e.g. seven', 'z.B. sieben',
        'a small credible number written as a word (five, seven, nine) matching the diagnostic you offer'),
    ],
    rules: [
      'HARD RULE: no leading questions. "Would more revenue help?" and anything shaped like it is banned.',
      'The question must be answerable honestly with "we do not check that" — that is the entire mechanism.',
      'No product name anywhere in the mail.',
      'Never assert they have the problem. Ask how they verify it.',
    ],
    prompt: `FRAMEWORK: Poke the Bear
One neutral question exposing a gap in their process → peer pattern → small diagnostic offer. No leading questions.`,
  },
  {
    id: 'audit-give-first',
    tier: 'play',
    icon: 'fa-clipboard-check',
    badge: L('Best for agencies & consulting', 'Beste Agentur- und Beratungsvariante'),
    name: L('Audit / Benchmark Give-First', 'Audit zuerst geben'),
    why: L(
      'You already did the work. The mail only asks permission to hand it over.',
      'Die Arbeit ist schon gemacht. Die Mail bittet nur um Erlaubnis, sie zu übergeben.',
    ),
    bestFor: L(
      'Consulting, agencies, security, recruiting, sales services, ops.',
      'Beratung, Agenturen, Security, Recruiting, Sales Services, Ops.',
    ),
    source: 'Cross-source synthesis: Gong / 30MPC / Outreach / Smartlead',
    steps: [
      S('Observable gap', 'Sichtbare Lücke', 3),
      S('Benchmark', 'Vergleichswert', 2),
      S('Proof of POV', 'Beweis der These', 2),
      S('Permission ask', 'Erlaubnisfrage', 2),
    ],
    subjectRule: 'Use "{{business_name}} benchmark" or the equivalent in the target language. Flat, factual, zero curiosity-gap tricks.',
    skeleton: `{{greeting}}

I looked at [[surface]] and noticed [[gap]].

Against [[benchmark]], that is likely costing [[consequence]].

I mapped the three changes I would test first and added two examples from [[peerType]].

Want me to send the one-pager?`,
    slots: [
      SLOT('surface', 'fa-desktop',
        'What you actually looked at', 'Was du dir angesehen hast',
        'e.g. their checkout flow on mobile', 'z.B. ihren Checkout-Flow auf Mobil',
        'name the exact externally observable surface or process you inspected — a page, a listing, a job ad, a load time, a sequence. It must be verifiable by the reader in under a minute. Max 10 words'),
      SLOT('gap', 'fa-crosshairs',
        'The specific gap', 'Die konkrete Lücke',
        'e.g. three required fields before guest checkout', 'z.B. drei Pflichtfelder vor dem Gast-Checkout',
        'state the specific gap with a detail or number, so it is obviously not a mail merge. Max 15 words'),
      SLOT('benchmark', 'fa-balance-scale',
        'Benchmark / peer set', 'Vergleichsgruppe',
        'e.g. the 20 DTC shops we measured this quarter', 'z.B. die 20 DTC-Shops aus diesem Quartal',
        'name the reference set the gap is measured against. Concrete and countable. Max 10 words'),
      SLOT('consequence', 'fa-hand-holding-usd',
        'Business consequence', 'Geschäftliche Folge',
        'e.g. around 8% of mobile carts', 'z.B. rund 8% der Mobil-Warenkörbe',
        'estimate the business consequence conservatively, scaled to this company\'s actual size. Use a range, never a hard promise. Max 10 words'),
      SLOT('peerType', 'fa-store',
        'Examples from which peer type', 'Beispiele von welchem Typ',
        'e.g. two other outdoor brands', 'z.B. zwei andere Outdoor-Marken',
        'name the peer type the two examples come from. Max 6 words'),
    ],
    rules: [
      'The gap must be real and externally checkable. If the research data does not support one, the {AI instruction} must say so and skip the mail rather than fabricate.',
      'Estimate conservatively. One inflated number kills the whole give-first premise.',
      'Ask permission to send. Never attach and never ask for a call.',
    ],
    prompt: `FRAMEWORK: Audit / Benchmark Give-First
Observable gap → benchmark → proof of your point of view → ask permission to send the one-pager.`,
  },
  {
    id: 'competitive-contrast',
    tier: 'play',
    icon: 'fa-exchange-alt',
    badge: L('Strong in incumbent markets', 'Stark bei bestehenden Anbietern'),
    name: L('Competitive Contrast / Switch Trigger', 'Wechsel-Anlass'),
    why: L(
      'They already bought something. Name the trade-off it forced, not the vendor.',
      'Sie haben schon gekauft. Benenne den Trade-off, den das erzwingt — nicht den Anbieter.',
    ),
    bestFor: L(
      'Software, agencies, managed services, payments, telecom, logistics.',
      'Software, Agenturen, Managed Services, Payments, Telekom, Logistik.',
    ),
    source: 'Cross-source synthesis: Gong / 30MPC / Outreach / Smartlead',
    steps: [
      S('Stack signal', 'Aktuelles Setup', 2),
      S('Concrete trade-off', 'Konkreter Haken', 3),
      S('Switch proof', 'Wechsel-Beweis', 2),
      S('Comparison offer', 'Vergleichsangebot', 2),
    ],
    subjectRule: 'Reference the category or the trade-off in 2–4 words. Never put the incumbent\'s brand name in the subject line.',
    skeleton: `{{greeting}}

Saw you are running [[incumbent]].

Solid choice for [[whatItIsGoodAt]] — the teams we talk to usually hit the wall at [[tradeoff]].

[[switchProof]] after moving that one piece, and kept everything else in place.

Want the [[comparisonAsset]]?`,
    slots: [
      SLOT('incumbent', 'fa-plug',
        'Incumbent / current setup', 'Aktueller Anbieter / Setup',
        'e.g. Shopify Plus with a custom checkout', 'z.B. Shopify Plus mit Custom Checkout',
        'identify the current vendor, stack or manual process from the research data (tech detection, job ads, case studies, footer badges). If it cannot be confirmed, omit this sentence — never guess a vendor'),
      SLOT('whatItIsGoodAt', 'fa-thumbs-up',
        'What it is genuinely good at', 'Worin es wirklich gut ist',
        'e.g. getting live fast', 'z.B. schnell live zu gehen',
        'give the incumbent honest credit for the thing it actually does well. Max 6 words'),
      SLOT('tradeoff', 'fa-compress-arrows-alt',
        'The trade-off it forces', 'Der erzwungene Haken',
        'e.g. every payment rule needs a developer', 'z.B. jede Payment-Regel braucht einen Entwickler',
        'name ONE specific structural trade-off of that setup at this company\'s scale — a limitation, not an insult. Max 12 words'),
      SLOT('switchProof', 'fa-route',
        'Switch proof', 'Wechsel-Beweis',
        'e.g. a similar brand cut rule changes to a day', 'z.B. eine ähnliche Marke kürzte Regeländerungen auf einen Tag',
        'describe a comparable company that changed only that one component, with the outcome. Emphasise that the rest of the stack stayed. Max 15 words'),
      SLOT('comparisonAsset', 'fa-table',
        'Comparison / migration asset', 'Vergleich / Umzugsplan',
        'e.g. side-by-side gap analysis', 'z.B. Side-by-Side-Gap-Analyse',
        'name the comparison, migration plan or gap analysis you offer to send. Max 8 words'),
    ],
    rules: [
      'Never disparage the incumbent. Credit it first, then name one structural trade-off.',
      'No unverifiable claims about a named competitor — trade-offs only, and only ones you can defend.',
      'If the research cannot confirm the incumbent, the {AI instruction} must drop the line rather than guess.',
    ],
    prompt: `FRAMEWORK: Competitive Contrast
Stack signal → honest credit → one structural trade-off → switch proof → offer the comparison.`,
  },

  // ══════════════════════════════ CLASSICS ══════════════════════════════
  {
    id: 'offer-led',
    tier: 'classic',
    icon: 'fa-crosshairs',
    name: L('Offer-Led', 'Angebotsgeführt'),
    why: L('The offer carries the mail. No fluff.', 'Das Angebot trägt die Mail. Kein Fülltext.'),
    steps: [
      S('Context', 'Kontext', 2),
      S('Problem', 'Problem', 2),
      S('Clear offer', 'Klares Angebot', 3),
      S('Soft CTA', 'Weiche Frage', 1),
    ],
    promptTemplate: `FRAMEWORK: Offer-Led
Structure the email EXACTLY like this:
1. CONTEXT — Use a {single brace instruction} to pull ONE specific observation from web research or their online presence. Be surgical — just one detail that shows you looked.
2. PROBLEM / OPPORTUNITY — Name a specific pain point. Use {instruction} to reference their actual situation from research data if possible.
3. ULTRA-CLEAR SOLUTION — Your offer in one sentence. No fluff. This is fixed text — the offer is the same for everyone.
4. SOFT CTA — Low-pressure ask ("Macht es Sinn, kurz zu schauen?" / "Worth a quick look?")
Why this works: no filler, directly in the customer's head, offer-driven not text-driven.`,
    prompt: `FRAMEWORK: Offer-Led
1. CONTEXT — One specific observation from their business.
2. PROBLEM / OPPORTUNITY — A concrete pain or opening.
3. ULTRA-CLEAR SOLUTION — The offer in one sentence.
4. SOFT CTA — Low-pressure ask.
Keep it to 2–5 sentences. Every word must earn its place.`,
  },
  {
    id: 'minimalist',
    tier: 'classic',
    icon: 'fa-compress-alt',
    name: L('Minimalist 3-Block', 'Drei Blöcke, minimal'),
    why: L('The 80% foundation: three blocks, no ballast.', 'Das 80%-Fundament: drei Blöcke, kein Ballast.'),
    steps: [
      S('Personal hook', 'Persönlicher Haken', 2),
      S('Offer', 'Angebot', 3),
      S('CTA', 'Abschlussfrage', 1),
    ],
    promptTemplate: `FRAMEWORK: Minimalist 3-Block
The foundation of 80% of all successful cold email campaigns. Structure:
1. PERSONALIZATION — Use a {single brace instruction} to reference something specific from web research, their website, or custom fields. One line max.
2. OFFER — Your value proposition in 1–2 sentences max. Fixed text — keep it consistent.
3. CTA — One simple question or ask.
Keep it to 3–5 sentences total.`,
    prompt: `FRAMEWORK: Minimalist 3-Block
1. PERSONALIZATION — One specific line about them.
2. OFFER — Value in 1–2 sentences.
3. CTA — One simple ask.
3–5 sentences total.`,
  },
  {
    id: 'pas',
    tier: 'classic',
    icon: 'fa-bullseye',
    name: L('Problem–Agitate–Solve', 'Problem–Verstärken–Lösen'),
    why: L('Name the pain, make it felt, then solve it.', 'Schmerz nennen, spürbar machen, dann lösen.'),
    steps: [
      S('Pain', 'Schmerz', 2),
      S('Amplify', 'Verstärken', 3),
      S('Solve', 'Lösen', 2),
    ],
    promptTemplate: `FRAMEWORK: Problem–Agitate–Solve (PAS)
1. PROBLEM — Use a {single brace instruction} to identify a real problem from web research. Be specific, not generic.
2. AGITATE — Amplify the pain with a {instruction} that estimates realistic impact based on their industry/size. What does this cost them?
3. SOLVE — Present your solution as the natural answer. Fixed text — your offer.
The pain must feel personal, not hypothetical.`,
    prompt: `FRAMEWORK: Problem–Agitate–Solve (PAS)
1. PROBLEM — A real, specific problem.
2. AGITATE — What it costs them.
3. SOLVE — Your offer as the natural answer.`,
  },
  {
    id: 'value-first',
    tier: 'classic',
    icon: 'fa-gift',
    name: L('Value-First', 'Wert zuerst'),
    why: L('Give something useful first. Trust, then ask.', 'Erst echten Nutzen geben. Dann Vertrauen, dann fragen.'),
    steps: [
      S('I made this for you', 'Ich hab dir etwas mitgebracht', 2),
      S('The value', 'Der Nutzen', 3),
      S('Want more?', 'Willst du mehr?', 1),
    ],
    promptTemplate: `FRAMEWORK: Value-First
1. "I MADE SOMETHING FOR YOU" — Use a {single brace instruction} to reference a specific insight from web research (audit, gap, lead, competitive note).
2. HERE'S THE VALUE — Use a {instruction} to summarize the finding in 1–2 sentences with specific details.
3. WANT MORE? — Soft offer to share more or discuss.
Give genuine value first.`,
    prompt: `FRAMEWORK: Value-First
1. Lead with a specific, useful insight about their business.
2. Summarize the finding in 1–2 sentences.
3. Soft offer to share more.`,
  },
  {
    id: 'trigger',
    tier: 'classic',
    icon: 'fa-bolt',
    name: L('Trigger Question', 'Auslöser-Frage'),
    why: L('One sharp question. Maximum speed.', 'Eine scharfe Frage. Maximale Geschwindigkeit.'),
    steps: [
      S('Trigger question', 'Auslöser-Frage', 4),
      S('Optional context', 'Optionaler Kontext', 1),
    ],
    promptTemplate: `FRAMEWORK: Trigger Question
1. TRIGGER QUESTION — One direct question that taps into an existing need. Use a {single brace instruction} only if research can sharpen it.
2. (OPTIONAL) Brief context — One sentence if the data adds something specific.
1–2 sentences total.`,
    prompt: `FRAMEWORK: Trigger Question
1. One direct question that taps an existing need.
2. Optional one-line context.
1–2 sentences total.`,
  },
  {
    id: 'before-after',
    tier: 'classic',
    icon: 'fa-arrows-alt-h',
    name: L('Before → After', 'Vorher → Nachher'),
    why: L('Show the gap. You are the bridge.', 'Die Lücke zeigen. Ihr seid die Brücke.'),
    steps: [
      S('Now', 'Jetzt', 2),
      S('Desired', 'Wunsch', 2),
      S('Bridge', 'Brücke', 2),
    ],
    promptTemplate: `FRAMEWORK: Before–After Bridge
1. CURRENT STATE — Use a {single brace instruction} to describe their actual current situation from research.
2. DESIRED STATE — Paint what "good" looks like. Can be fixed text.
3. BRIDGE — Your offer gets them from A to B. Fixed text.`,
    prompt: `FRAMEWORK: Before–After Bridge
1. Current state — their reality.
2. Desired state — what good looks like.
3. Bridge — your offer.`,
  },
  {
    id: 'personal-connection',
    tier: 'classic',
    icon: 'fa-handshake',
    name: L('Personal Connection', 'Persönliche Verbindung'),
    why: L('Must feel 1:1, not like a campaign.', 'Muss sich 1:1 anfühlen, nicht nach Kampagne.'),
    steps: [
      S('Personal hook', 'Persönlicher Haken', 2),
      S('Insight', 'Erkenntnis', 2),
      S('Open question', 'Offene Frage', 2),
    ],
    promptTemplate: `FRAMEWORK: Personal Connection
1. REAL PERSONAL CONNECTION — Use a {single brace instruction} to find something specific the decision maker said, posted, or published.
2. BRIEF INSIGHT — An authentic observation, not flattery.
3. OPEN CONVERSATION — A thoughtful question.
Must NOT feel like cold email.`,
    prompt: `FRAMEWORK: Personal Connection
1. Something specific they said or published.
2. A real observation, not flattery.
3. A thoughtful question.`,
  },
  {
    id: 'aida',
    tier: 'classic',
    icon: 'fa-layer-group',
    name: L('AIDA', 'AIDA'),
    why: L('Classic path, kept short and not salesy.', 'Klassischer Weg, kurz und nicht verkäuferisch.'),
    steps: [
      S('Attention', 'Aufmerksamkeit', 2),
      S('Interest', 'Interesse', 2),
      S('Desire', 'Wunsch', 2),
      S('Action', 'Handlung', 1),
    ],
    promptTemplate: `FRAMEWORK: AIDA (Cold Email Version)
1. ATTENTION — {single brace instruction} with something specific from research. Not a generic compliment.
2. INTEREST — Connect their situation to why this matters to them.
3. DESIRE — Concrete benefit. Numbers/timeframes if possible.
4. ACTION — Soft CTA.
4–5 sentences max. Not salesy.`,
    prompt: `FRAMEWORK: AIDA
1. Attention — specific hook.
2. Interest — why it matters to them.
3. Desire — concrete benefit.
4. Action — soft CTA.
4–5 sentences max.`,
  },
  {
    id: 'benefit-stack',
    tier: 'classic',
    icon: 'fa-coins',
    name: L('Benefit Stack', 'Nutzen stapeln'),
    why: L('Each line stronger than the last.', 'Jede Zeile stärker als die davor.'),
    steps: [
      S('Main benefit', 'Hauptnutzen', 2),
      S('Amplify', 'Verstärken', 2),
      S('Concrete result', 'Konkretes Ergebnis', 2),
    ],
    promptTemplate: `FRAMEWORK: Benefit Stack
1. MAIN BENEFIT — Core value in one sentence. Fixed text.
2. AMPLIFICATION — Remove an objection or add a "without X". Tailor via {instruction} if useful.
3. CONCRETE RESULT — Specific numbers/outcome, ideally sized to them.
3 lines, each one stronger than the last.`,
    prompt: `FRAMEWORK: Benefit Stack
1. Main benefit — one sentence.
2. Amplify / remove an objection.
3. Concrete numbered result.`,
  },
  {
    id: 'specificity',
    tier: 'classic',
    icon: 'fa-ruler-combined',
    name: L('Specificity', 'Konkret statt vage'),
    why: L('Vague claims die. Numbers get replies.', 'Vage Behauptungen sterben. Zahlen holen Antworten.'),
    steps: [
      S('Vague claim', 'Vage Behauptung', 1),
      S('Concrete number', 'Konkrete Zahl', 3),
      S('Their scale', 'Ihre Größenordnung', 2),
    ],
    promptTemplate: `FRAMEWORK: Specificity
Replace EVERY vague claim with a specific one.
- BAD: "more leads" → GOOD: "12–18 qualified conversations per month"
- BAD: "fast results" → GOOD: "first qualified meetings within 14 days"
Use {single brace instructions} to insert lead-specific numbers where generic stats would go.
Every benefit, every claim — concrete.`,
    prompt: `FRAMEWORK: Specificity
Replace every vague claim with a concrete number, timeframe, or outcome sized to the recipient.`,
  },
  {
    id: 'proof-credibility',
    tier: 'classic',
    icon: 'fa-shield-alt',
    name: L('Proof & Credibility', 'Beweis & Glaubwürdigkeit'),
    why: L('Every claim gets a quiet proof, not a pitch.', 'Jede Behauptung bekommt einen stillen Beweis, keinen Verkaufsspruch.'),
    steps: [
      S('Claim', 'Behauptung', 2),
      S('Woven-in proof', 'Eingewobener Beweis', 3),
    ],
    promptTemplate: `FRAMEWORK: Proof & Credibility
1. CLAIM — What you do / the result. Fixed text.
2. PROOF — Woven in naturally. Use a {instruction} to match their industry. Never "download our case study".
GOOD: "For similar B2B companies, this generates 12–18 conversations monthly"
BAD: "Download our case study"`,
    prompt: `FRAMEWORK: Proof & Credibility
State the claim, then weave in quiet proof. No case-study pitch.`,
  },
  {
    id: 'logical-flow',
    tier: 'classic',
    icon: 'fa-project-diagram',
    name: L('Logical Flow', 'Logischer Aufbau'),
    why: L('An argument, not a teaser. Each sentence earns the next.', 'Ein Argument, kein Anreißer. Jeder Satz begründet den nächsten.'),
    steps: [
      S('Situation', 'Situation', 2),
      S('Problem', 'Problem', 2),
      S('Solution', 'Lösung', 2),
      S('Result', 'Ergebnis', 2),
    ],
    promptTemplate: `FRAMEWORK: Logical Flow
1. SITUATION — {instruction} for their actual current state from research.
2. PROBLEM — Concrete cost or friction.
3. SOLUTION — How you solve it, one sentence. Fixed text.
4. RESULT — What they get, sized to them if possible.`,
    prompt: `FRAMEWORK: Logical Flow
Situation → Problem → Solution → Result. Each step builds on the last.`,
  },
  {
    id: 'clarity',
    tier: 'classic',
    icon: 'fa-glasses',
    name: L('Clarity', 'Klarheit'),
    why: L('A smart teenager must get every sentence.', 'Ein kluger Teenager muss jeden Satz verstehen.'),
    steps: [
      S('Simple words', 'Einfache Wörter', 2),
      S('Short sentences', 'Kurze Sätze', 2),
      S('No jargon', 'Kein Jargon', 2),
    ],
    promptTemplate: `FRAMEWORK: Clarity Over Cleverness
Write so a 12-year-old understands it.
- Replace every complex word with a simple one
- No jargon unless they use it daily
- If you can say it in 5 words, don't use 15
{AI instructions} must also ask for simple, clear output.`,
    prompt: `FRAMEWORK: Clarity Over Cleverness
Simple words. Short sentences. No jargon. Every line must pass a teenager test.`,
  },
];

/**
 * Shared doctrine for the 2026 plays. All six were selected because they trade
 * "give me time" for "let me give you something", which is what still gets
 * replies once inboxes are saturated.
 */
const GIVE_FIRST_DOCTRINE = `SHARED DOCTRINE FOR THIS FRAMEWORK FAMILY:
- The close asks permission to SEND something. It never asks for calendar time, "15 minutes", "a quick call" or "a chat".
- The close must be answerable with a single word. One ask per mail, never two.
- Specificity beats adjectives everywhere: a number, a name or a date instead of "significant", "leading", "innovative".
- Nothing may be invented. Anything the research cannot confirm must be skipped, not guessed.
- No compliment openers, no "hope you are well", no "I came across your website".`;

const SLOT_TOKEN = /\[\[([a-zA-Z0-9_]+)\]\]/g;

function pickLang(value, lang) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value.English || '';
}

/**
 * Resolves the skeleton's [[tokens]] into either the user's fixed copy or a
 * placeholder marker, and reports which slots stayed empty.
 * Used by the UI preview and by the prompt compiler so both stay in sync.
 */
export function resolveSkeleton(fw, slotValues = {}, lang = 'English') {
  if (!fw?.skeleton) return { segments: [], filled: [], auto: [] };
  const slots = fw.slots || [];
  const byKey = new Map(slots.map((s) => [s.key, s]));
  const filled = [];
  const auto = [];
  const segments = [];
  let cursor = 0;

  SLOT_TOKEN.lastIndex = 0;
  let match = SLOT_TOKEN.exec(fw.skeleton);
  while (match) {
    if (match.index > cursor) {
      segments.push({ type: 'text', value: fw.skeleton.slice(cursor, match.index) });
    }
    const key = match[1];
    const slot = byKey.get(key);
    const value = (slotValues[key] || '').trim();
    if (value) {
      segments.push({ type: 'filled', key, value });
      if (slot) filled.push({ slot, value });
    } else {
      segments.push({ type: 'auto', key, label: pickLang(slot?.label, lang) || key });
      if (slot) auto.push(slot);
    }
    cursor = match.index + match[0].length;
    match = SLOT_TOKEN.exec(fw.skeleton);
  }
  if (cursor < fw.skeleton.length) {
    segments.push({ type: 'text', value: fw.skeleton.slice(cursor) });
  }
  return { segments, filled, auto };
}

/**
 * Compiles everything the generator AI needs to run one framework:
 * provenance, beat structure, the literal skeleton, a per-slot briefing that
 * maps filled slots to fixed copy and empty slots to {single brace}
 * instructions, plus the framework's hard rules.
 */
export function buildFrameworkPromptBlock(fw, slotValues = {}, options = {}) {
  if (!fw) return '';
  const lang = options.language === 'German' ? 'German' : 'English';
  const targetLanguage = options.targetLanguage || options.language || 'English';
  const forSubject = options.forSubject === true;
  const allowBraces = options.allowBraces !== false;

  const name = pickLang(fw.name, 'English');

  if (fw.tier !== 'play') {
    const body = fw.promptTemplate || fw.prompt || '';
    return `=== EMAIL FRAMEWORK: ${name} ===\n${body}`;
  }

  const lines = [`=== EMAIL FRAMEWORK: ${name} ===`];
  if (fw.badge) lines.push(`Reputation: ${pickLang(fw.badge, 'English')}`);
  if (fw.source) lines.push(`Provenance: ${fw.source}`);
  if (fw.bestFor) lines.push(`Built for: ${pickLang(fw.bestFor, 'English')}`);
  if (fw.why) lines.push(`Core idea: ${pickLang(fw.why, 'English')}`);

  if (forSubject) {
    lines.push('', 'SUBJECT LINE RULE FOR THIS FRAMEWORK:');
    lines.push(fw.subjectRule || 'Two to four words taken from the prospect\'s own world.');
    const { filled } = resolveSkeleton(fw, slotValues, lang);
    if (filled.length) {
      lines.push('', 'FACTS THE USER CONFIRMED (use these to make the subject concrete):');
      filled.forEach(({ slot, value }) => {
        lines.push(`- ${pickLang(slot.label, 'English')}: ${value}`);
      });
    }
    return lines.join('\n');
  }

  const steps = fw.steps || [];
  // The sentence budget rides along with the beats on purpose. As a lone bullet
  // further down the prompt it got ignored — next to the structure the model is
  // already following, it holds.
  const plan = frameworkLengthPlan(fw, options.lengthScale || 'natural');
  const expandsBeyondSkeleton = plan.steps.some((s) => s.sentences > 1);

  if (steps.length) {
    lines.push('', 'BEAT STRUCTURE — write the body in exactly this order, one beat per block. '
      + `Every beat gets at least one sentence, and the whole body must not exceed ${plan.max} sentences. `
      + 'Per-beat allowance:');
    steps.forEach((s, i) => {
      const n = plan.steps[i].sentences;
      lines.push(`${i + 1}. ${pickLang(s.label, 'English').toUpperCase()} — up to ${n} sentence${n === 1 ? '' : 's'}`);
    });
    lines.push(`The upper limit of ${plan.max} is binding, the allowances are headroom. `
      + 'Use a second sentence on a beat only when you have real substance for it — a consequence, '
      + 'a concrete number, a second angle. Shorter is better than padded, but no beat may be dropped. '
      + 'A {single brace AI instruction} counts as the one sentence it will produce, however long the '
      + 'instruction text itself is.');
  }

  if (fw.skeleton) {
    lines.push('', `SKELETON — follow this shape and rhythm, but write it in ${targetLanguage} and in your own words. Never ship this wording verbatim:`);
    lines.push(fw.skeleton);
    if (expandsBeyondSkeleton) {
      // Without this the skeleton wins over the budget: the model copies its
      // one-sentence-per-beat rhythm and quietly ignores the larger allowance.
      const wider = plan.steps
        .filter((s) => s.sentences > 1)
        .map((s) => `${s.label} → ${s.sentences}`)
        .join(', ');
      lines.push(`The skeleton shows every beat at one sentence, which is always a valid answer. These beats may run longer if the material earns it: ${wider}. Take that room only for substance, never to restate what the beat already said.`);
    }
    lines.push('The {{double brace}} variables in the skeleton are illustrative only. Use exactly the placeholders the DYNAMIC PLACEHOLDERS rule allows — drop or rephrase any other one, including {{greeting}}, instead of inventing it.');
  }

  const { filled, auto } = resolveSkeleton(fw, slotValues, lang);

  if (filled.length) {
    lines.push('', 'CONFIRMED INPUTS — the user supplied these. Treat them as fact and write them as PLAIN TEXT (no braces), translated naturally into the email language:');
    filled.forEach(({ slot, value }) => {
      lines.push(`- [[${slot.key}]] (${pickLang(slot.label, 'English')}) = "${value}"`);
    });
  }

  if (auto.length) {
    if (allowBraces) {
      lines.push('', `OPEN SLOTS — the user left these blank on purpose. Replace each one with a {single brace AI instruction} written in ${targetLanguage}. Each instruction must name the data source, state exactly what to output, give a length limit, an example of good output, and a fallback when the data is missing:`);
      auto.forEach((slot) => {
        lines.push(`- [[${slot.key}]] (${pickLang(slot.label, 'English')}) → instruct the downstream per-lead AI to: ${slot.auto}`);
      });
    } else {
      lines.push('', 'OPEN SLOTS — the user left these blank. Fill them from the context you have, and drop the sentence entirely if you cannot fill it credibly:');
      auto.forEach((slot) => {
        lines.push(`- ${pickLang(slot.label, 'English')}: ${slot.auto}`);
      });
    }
  }

  if (fw.rules?.length) {
    lines.push('', 'HARD RULES FOR THIS FRAMEWORK (violating one breaks the play):');
    fw.rules.forEach((r) => lines.push(`- ${typeof r === 'string' ? r : pickLang(r, 'English')}`));
  }

  lines.push('', GIVE_FIRST_DOCTRINE);
  return lines.join('\n');
}

/* ═══════════════════════════════════════════════════════════════════════
   LENGTH
   ═══════════════════════════════════════════════════════════════════════

   Length is RELATIVE to the chosen framework, never absolute.

   Every framework already declares a sentence weight per beat, so it carries
   its own natural size: Trigger lives on 5 sentences, SPPO needs 10. Asking
   for "1–2 sentences" on a four-beat play is incoherent — something has to be
   dropped, and the model picks which beat dies.

   So the three settings scale that budget instead of overriding it, and no
   beat can ever fall below one sentence. Squeezing SPPO gives you a tight
   SPPO, not a broken one.
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * Sentences per beat, as a multiple of the canonical "one sentence per beat".
 *
 * The skeletons are the reference: every one of them spends exactly one
 * sentence per beat, and that is the tightest a play reads well. So `tight`
 * IS the skeleton, and the looser settings buy extra sentences — handed to the
 * heaviest beats first, which is what `step.weight` is for.
 */
const LENGTH_FACTORS = { tight: 1, natural: 1.3, roomy: 1.9 };

/** Sizes used when no framework is selected and there are no beats to scale. */
const FREESTYLE_LENGTH = {
  tight: { min: 2, max: 3 },
  natural: { min: 4, max: 6 },
  roomy: { min: 7, max: 10 },
};

/**
 * Spread `target` sentences over the beats: one each, then the remainder to the
 * heaviest beats first. Ties break left-to-right so the opening beat wins.
 */
function allocateSentences(steps, target) {
  const alloc = steps.map(() => 1);
  const byWeight = steps
    .map((s, i) => ({ i, weight: s.weight || 1 }))
    .sort((a, b) => b.weight - a.weight || a.i - b.i);

  let remaining = Math.max(0, target - steps.length);
  let cursor = 0;
  while (remaining > 0) {
    alloc[byWeight[cursor % byWeight.length].i] += 1;
    remaining -= 1;
    cursor += 1;
  }
  return alloc;
}

/**
 * Map any stored length value onto the three current scales.
 * Older templates and presets still carry 'super-short' / 'short' / 'medium' / 'long'.
 */
export function normalizeLengthScale(value) {
  if (value === 'tight' || value === 'natural' || value === 'roomy') return value;
  if (value === 'super-short' || value === 'short') return 'tight';
  if (value === 'long') return 'roomy';
  return 'natural';
}

/**
 * Sentence plan for one framework at one length setting.
 *
 * @param {Object|null} fw framework, or null for freestyle generation
 * @param {string} scale 'tight' | 'natural' | 'roomy'
 * @param {string} [lang] language for the returned beat labels
 * @returns {{scale: string, min: number, max: number, steps: Array<{label: string, sentences: number}>}}
 */
export function frameworkLengthPlan(fw, scale = 'natural', lang = 'English') {
  const key = normalizeLengthScale(scale);
  const steps = fw?.steps || [];

  if (!steps.length) {
    return { scale: key, ...FREESTYLE_LENGTH[key], steps: [] };
  }

  const target = Math.round(steps.length * LENGTH_FACTORS[key]);
  const alloc = allocateSentences(steps, target);
  const planned = steps.map((s, i) => ({
    label: pickLang(s.label, lang),
    sentences: alloc[i],
  }));

  // An ALLOWANCE, not a quota. One sentence per beat is the floor — that is the
  // constraint that matters, and it is what stops a four-beat play from being
  // squeezed into one line. The upper end is headroom the model may use when it
  // has real substance for it.
  //
  // Enforcing the upper end as a minimum was tried and produced padding: several
  // skeletons deliberately carry two slots in a single sentence, so demanding
  // "6 sentences" from a three-beat play just inflates the copy.
  return {
    scale: key,
    min: steps.length,
    max: alloc.reduce((sum, n) => sum + n, 0),
    steps: planned,
  };
}

/**
 * The length instruction for the generator prompt. Beat-by-beat when a
 * framework is active, because "6 sentences" alone tells the model nothing
 * about where those sentences should go.
 */
export function buildLengthRule(fw, scale = 'natural', lang = 'English') {
  const plan = frameworkLengthPlan(fw, scale, lang);
  if (!plan.steps.length) {
    return `${plan.min}-${plan.max} sentences total.`;
  }
  // With a framework active the per-beat split lives in the BEAT STRUCTURE
  // section, so this stays a short reminder instead of repeating the table.
  return `${plan.min}-${plan.max} sentences — the per-beat allowance is in the `
    + 'BEAT STRUCTURE section above. Never fewer than one sentence per beat, never more than '
    + `${plan.max} in total.`;
}

export function localizeFramework(fw, language) {
  const lang = language === 'German' ? 'German' : 'English';
  return {
    ...fw,
    name: fw.name[lang] || fw.name.English,
    why: fw.why[lang] || fw.why.English,
    badge: fw.badge ? (fw.badge[lang] || fw.badge.English) : '',
    bestFor: fw.bestFor ? (fw.bestFor[lang] || fw.bestFor.English) : '',
    steps: (fw.steps || []).map((s) => ({
      label: s.label?.[lang] || s.label?.English || s[lang] || s.English || '',
      weight: s.weight || 2,
    })),
    slots: (fw.slots || []).map((s) => ({
      ...s,
      label: s.label?.[lang] || s.label?.English || '',
      placeholder: s.placeholder?.[lang] || s.placeholder?.English || '',
    })),
  };
}

export function getFrameworkById(id) {
  return emailFrameworks.find((f) => f.id === id) || null;
}

export default emailFrameworks;
